import { useMemo } from 'react';
import { formatHeatmapSlotRange, HeatmapGridCell } from '@/components/HeatmapGridCell';
import { TimezoneCountryStateFilter } from '@/components/TimezoneCountryStateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HEATMAP_TIMEZONES } from '@/lib/heatmapTimezones';
import { cn } from '@/lib/utils';
import { DashboardComingSoonPanel } from './DashboardComingSoonPanel';
import type { DashboardActivityHeatmapResponse } from '../api';

const DAYS = [
  { short: 'Mon', full: 'Monday' },
  { short: 'Tue', full: 'Tuesday' },
  { short: 'Wed', full: 'Wednesday' },
  { short: 'Thu', full: 'Thursday' },
  { short: 'Fri', full: 'Friday' },
  { short: 'Sat', full: 'Saturday' },
  { short: 'Sun', full: 'Sunday' },
] as const;

/** Figma: 2-hour slots from 12 AM → 10 PM. */
const TIME_SLOTS = [
  { label: '12 AM', startHour: 0 },
  { label: '2 AM', startHour: 2 },
  { label: '4 AM', startHour: 4 },
  { label: '6 AM', startHour: 6 },
  { label: '8 AM', startHour: 8 },
  { label: '10 AM', startHour: 10 },
  { label: '12 PM', startHour: 12 },
  { label: '2 PM', startHour: 14 },
  { label: '4 PM', startHour: 16 },
  { label: '6 PM', startHour: 18 },
  { label: '8 PM', startHour: 20 },
  { label: '10 PM', startHour: 22 },
] as const;

/** Figma sequential blues (Low → High). */
const HEAT_COLORS = ['#1e2433', '#243556', '#2f5f9a', '#3b82f6', '#60a5fa'] as const;

export { HEATMAP_TIMEZONES };

function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return HEAT_COLORS[0];
  const t = value / max;
  if (t < 0.2) return HEAT_COLORS[0];
  if (t < 0.4) return HEAT_COLORS[1];
  if (t < 0.6) return HEAT_COLORS[2];
  if (t < 0.8) return HEAT_COLORS[3];
  return HEAT_COLORS[4];
}

/**
 * User Activity heatmap — Figma packing: days on X, 2-hour slots on Y, Low→High legend.
 */
export function DashboardActivityHeatmap({
  data,
  isLoading,
  timezone,
  onTimezoneChange,
  className,
}: {
  data?: DashboardActivityHeatmapResponse | null;
  isLoading?: boolean;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  className?: string;
}) {
  const status = data?.status ?? 'COMING_SOON';
  const showShell = !isLoading && (status === 'COMING_SOON' || status === 'UNAVAILABLE');

  const { grid, max } = useMemo(() => {
    // [slotIndex][dayIndex] — 12 × 7
    const cells = Array.from({ length: TIME_SLOTS.length }, () =>
      Array.from({ length: 7 }, () => 0),
    );
    let peak = 0;
    for (const cell of data?.cells ?? []) {
      const dow = cell.dayOfWeek;
      const hour = cell.hour;
      const count = Number(cell.count) || 0;
      if (dow == null || hour == null || dow < 1 || dow > 7 || hour < 0 || hour > 23) continue;
      const slot = Math.floor(hour / 2);
      const day = dow - 1;
      cells[slot]![day]! += count;
      if (cells[slot]![day]! > peak) peak = cells[slot]![day]!;
    }
    return { grid: cells, max: peak };
  }, [data?.cells]);

  if (showShell) {
    return (
      <DashboardComingSoonPanel
        title="User Activity by Time of Day"
        variant={status === 'UNAVAILABLE' ? 'unavailable' : 'comingSoon'}
        description={
          status === 'UNAVAILABLE'
            ? 'PostHog query unavailable — check tipmi-admin-api Query credentials.'
            : 'Heat map of qualifying activity by day and hour (PostHog).'
        }
        {...(className ? { className } : {})}
      />
    );
  }

  return (
    <Card className={cn('border-border/40 bg-[#141416] shadow-none', className)}>
      <CardHeader className="space-y-3 p-5 pb-3">
        <CardTitle
          className="text-base font-semibold tracking-tight text-white"
          title={
            data?.dataSource === 'CACHE'
              ? 'Served from MySQL cache'
              : data?.dataSource === 'LIVE'
                ? 'Live PostHog query'
                : undefined
          }
        >
          User Activity by Time of Day
        </CardTitle>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TimezoneCountryStateFilter
            timezone={timezone}
            onTimezoneChange={onTimezoneChange}
            ariaLabel="Heatmap timezone"
            detail="Heatmap timezone"
            className="w-full min-w-[180px] sm:w-[280px]"
          />
          <div
            className="flex items-center gap-2 text-[11px] text-muted-foreground"
            aria-label="Activity intensity legend"
          >
            <span>Low</span>
            <div className="flex items-center gap-1">
              {HEAT_COLORS.map((c) => (
                <span
                  key={c}
                  className="inline-block size-3.5 rounded-sm"
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <span>High</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-2">
        {isLoading ? (
          <div className="flex h-[320px] items-center justify-center text-[11px] text-muted-foreground">
            Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[480px] gap-x-1.5 gap-y-1.5"
              style={{
                gridTemplateColumns: `3.25rem repeat(7, minmax(0, 1fr))`,
              }}
            >
              {TIME_SLOTS.map((slot, slotIdx) => (
                <div key={slot.label} className="contents">
                  <div className="flex h-7 items-center justify-end pr-1 text-[11px] tabular-nums text-muted-foreground">
                    {slot.label}
                  </div>
                  {DAYS.map((day, dayIdx) => {
                    const value = grid[slotIdx]![dayIdx] ?? 0;
                    const shareOfPeak =
                      max > 0 && value > 0
                        ? `${Math.round((value / max) * 100)}% of peak`
                        : undefined;
                    return (
                      <HeatmapGridCell
                        key={`${day.short}-${slot.label}`}
                        dayLabel={day.full}
                        timeLabel={formatHeatmapSlotRange(slot.startHour, timezone)}
                        value={value}
                        valueNoun="events"
                        {...(shareOfPeak != null ? { detail: shareOfPeak } : {})}
                        className="h-7 rounded-md"
                        style={{ backgroundColor: heatColor(value, max) }}
                      />
                    );
                  })}
                </div>
              ))}

              <div />
              {DAYS.map((day) => (
                <div key={day.short} className="pt-1 text-center text-[11px] font-medium text-muted-foreground">
                  {day.short}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
